/**
 * Circles of Care · public redemption page
 *
 * Feb 23 2026. Recipients receive a link like
 * `https://…/circles/redeem/{TOKEN}`. On landing they enter their
 * email and we mint / bind their Foundations access. Copy is warm,
 * short and never mentions Stripe or payments.
 */
import React, { useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import SoulumoWord from "../../components/SoulumoWord";

const API = process.env.REACT_APP_BACKEND_URL;

const TOK = {
  bg: "radial-gradient(ellipse at 50% 22%, #1a2e50 0%, #0d1f3a 50%, #060e1f 85%)",
  cream: "#f5ecd9",
  creamStrong: "rgba(245,236,217,0.94)",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.5)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.9)",
  goldMist: "rgba(224,169,122,0.24)",
  goldGlow: "rgba(224,169,122,0.32)",
};
const SERIF = '"Playfair Display", "Cormorant Garamond", serif';
const SANS = '"Manrope", "Helvetica Neue", Helvetica, Arial, sans-serif';

export default function CirclesRedeemPage() {
  const nav = useNavigate();
  const { token } = useParams();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [claimed, setClaimed] = useState(null);

  const submit = async () => {
    if (!email.trim()) { setError("Please add your email."); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/api/admin/circles/recipient/${token}/redeem`,
        { email: email.trim() },
      );
      setClaimed(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "This link isn't quite right. Please check the URL.");
    } finally {
      setBusy(false);
    }
  };

  if (claimed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: TOK.bg, color: TOK.cream }}>
        <div className="max-w-md text-center" data-testid="circles-redeem-success">
          <SoulumoWord size="sm" />
          <div
            className="mt-4 uppercase"
            style={{ color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem", letterSpacing: "0.4em" }}
          >
            Circles of Care
          </div>
          <h1
            className="mt-6 italic"
            style={{ fontFamily: SERIF, fontSize: "clamp(1.75rem, 6vw, 2.2rem)", fontWeight: 300 }}
          >
            A light has been placed for you{claimed.recipient_name ? `, ${claimed.recipient_name}` : ""}.
          </h1>
          {claimed.donor_display && (
            <p className="mt-4" style={{ color: TOK.creamStrong, fontFamily: SANS, fontSize: "1rem", lineHeight: 1.55 }}>
              From {claimed.donor_display}.
            </p>
          )}
          {claimed.note && (
            <p className="mt-3 italic" style={{ color: TOK.creamDim, fontFamily: SERIF, fontSize: "1.1rem" }}>
              "{claimed.note}"
            </p>
          )}
          <button
            onClick={() => nav("/foundations")}
            className="mt-8 rounded-full"
            style={{
              background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
              border: "1px solid #f5e0c5", color: "#1a0f05",
              fontFamily: SANS, fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.22em",
              textTransform: "uppercase", padding: "16px 30px", minHeight: 52,
              boxShadow: `0 12px 44px ${TOK.goldGlow}`,
            }}
            data-testid="circles-redeem-open-foundations"
          >
            Open Soulumo Foundations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: TOK.bg, color: TOK.cream }}>
      <div className="max-w-md text-center" data-testid="circles-redeem-page">
        <SoulumoWord size="sm" />
        <div
          className="mt-4 uppercase"
          style={{ color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem", letterSpacing: "0.4em" }}
        >
          Circles of Care
        </div>
        <h1
          className="mt-6 italic"
          style={{ fontFamily: SERIF, fontSize: "clamp(1.75rem, 6vw, 2.2rem)", fontWeight: 300 }}
        >
          A light is waiting for you.
        </h1>
        <p
          className="mt-4"
          style={{ color: TOK.creamStrong, fontFamily: SANS, fontSize: "1rem", lineHeight: 1.55 }}
        >
          Add your email and we'll open Soulumo Foundations for you — a small, quiet space of
          care to press play on whenever you need it.
        </p>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email"
          className="mt-8 w-full rounded-full px-5 py-3 text-center"
          style={{
            background: "rgba(245,236,217,0.06)",
            border: `1px solid ${TOK.goldMist}`, color: TOK.cream,
            fontFamily: SANS, fontSize: "1rem", outline: "none",
          }}
          data-testid="circles-redeem-email"
        />
        {error && (
          <p className="mt-3" style={{ color: "#e07a6a", fontFamily: SANS, fontSize: "0.9rem" }} data-testid="circles-redeem-error">
            {error}
          </p>
        )}
        <button
          onClick={submit}
          disabled={busy}
          className="mt-4 w-full rounded-full"
          style={{
            background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
            border: "1px solid #f5e0c5", color: "#1a0f05",
            fontFamily: SANS, fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.22em",
            textTransform: "uppercase", padding: "16px 30px", minHeight: 56,
            boxShadow: `0 12px 44px ${TOK.goldGlow}`,
            opacity: busy ? 0.65 : 1,
          }}
          data-testid="circles-redeem-submit"
        >
          {busy ? "Opening…" : "Open my Foundations"}
        </button>
      </div>
    </div>
  );
}
